import { useEffect, useState, type ReactNode } from 'react';
import {
  getSiteAuthStatus,
  hasSiteAccessToken,
  loginSite,
} from '../services/siteAuth';

interface SiteAccessGateProps {
  children: ReactNode;
}

export function SiteAccessGate({ children }: SiteAccessGateProps) {
  const [checking, setChecking] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [hint, setHint] = useState('获取访问口令，请联系 Ming 管理员');
  const [accessKey, setAccessKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;

    getSiteAuthStatus()
      .then((status) => {
        if (!alive) return;
        setEnabled(status.enabled);
        setHint(status.hint || '获取访问口令，请联系 Ming 管理员');
        setAuthenticated(!status.enabled || hasSiteAccessToken());
      })
      .catch(() => {
        if (!alive) return;
        setEnabled(true);
        setAuthenticated(hasSiteAccessToken());
        setError('服务连接异常，请稍后重试');
      })
      .finally(() => {
        if (alive) setChecking(false);
      });

    const handleExpired = () => {
      setEnabled(true);
      setAuthenticated(false);
      setError('访问已过期，请重新输入口令');
    };

    window.addEventListener('downinfo-site-auth-expired', handleExpired);

    return () => {
      alive = false;
      window.removeEventListener('downinfo-site-auth-expired', handleExpired);
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedKey = accessKey.trim();
    if (!trimmedKey) return;

    setLoading(true);
    setError(null);

    try {
      await loginSite(trimmedKey);
      setAuthenticated(true);
      setAccessKey('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '访问口令错误');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="h-10 w-10 rounded-full border-2 border-border border-t-primary animate-spin" />
      </div>
    );
  }

  if (!enabled || authenticated) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-surface border border-border rounded-lg shadow-lg p-6 sm:p-8">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-primary font-heading font-bold">
              ID
            </div>
            <h1 className="font-display text-2xl font-bold text-text">DownInfoAssistant</h1>
            <p className="mt-2 text-sm text-text-muted">{hint}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-text-secondary">访问口令</span>
              <input
                type="password"
                value={accessKey}
                onChange={(event) => setAccessKey(event.target.value)}
                placeholder="请输入访问口令"
                autoComplete="current-password"
                autoFocus
                className="w-full rounded-md border border-border bg-surface px-4 py-3 text-text placeholder:text-text-muted focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </label>

            {error && (
              <div className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !accessKey.trim()}
              className="w-full rounded-md bg-primary px-4 py-3 font-medium text-white transition-fast hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? '验证中...' : '进入系统'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
